import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Table,
  Form,
} from "react-bootstrap";
import {
  CreditCard,
  Check2Circle,
  Person,
  Building,
  GeoAlt,
  Phone,
  Envelope,
  Globe,
  Cash,
  Pencil,
  X,
  Check,
} from "react-bootstrap-icons";
import FromPageIndicator from "../Common/FromPageIndicator";
import { useNavigate } from "react-router-dom";

const UserDetailsComponent = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previousAbstracts, setPreviousAbstracts] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();

  const fetchPreviousAbstracts = async (email) => {
    try {
      const absResponse = await fetch(
        `https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Records_AbstractRecords?email=${email}`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
          },
        }
      );
      if (!absResponse.ok) {
        throw new Error("Some error occured");
      }
      const previousAbstracts = await absResponse.json();
      console.log(previousAbstracts);

      const parsedAbstracts = previousAbstracts.map((abstract) => ({
        ...abstract,
        correspondingAuthor: JSON.parse(abstract.correspondingAuthor),
        authors: JSON.parse(abstract.authors),
        abstract: JSON.parse(abstract.abstract),
      }));

      setPreviousAbstracts(parsedAbstracts);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const RegUserID =
          sessionStorage.getItem("userId") || localStorage.getItem("userId");

        const response = await fetch(
          `https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList/${RegUserID}`
        );
        const data = await response.json();
        setUserData(data);
        setEditedData(data); // Initialize editedData with current data
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };
    const RegUserMail =
      sessionStorage.getItem("emailId") || localStorage.getItem("emailId");
    fetchUserData();
    fetchPreviousAbstracts(RegUserMail);
  }, []);

  const getCategoryLabel = (category) => {
    const categories = {
      ampi: "AMPI Member",
      "non-ampi": "Non AMPI Member",
      associate: "Associate Delegate",
      "senior-60": "Senior AMPI Member (60+)",
      "senior-75": "Senior AMPI Member (75+)",
      student: "Student (Medical Physics)",
      overseas: "Overseas Delegate",
      "oncology-or-physicist": "Oncology or Physicist",
      "oncologist&physicist": "Oncology and Physicist",
    };
    return categories[category] || category;
  };

  const getPaymentStatusBadge = (status) => {
    if (status === "PAID") {
      return (
        <Badge bg="success" className="px-3 py-2">
          Payment Completed <Check2Circle size={18} />
        </Badge>
      );
    } else {
      return (
        <Badge bg="warning" text="dark" className="px-3 py-2">
          Payment Pending <CreditCard size={18} />
        </Badge>
      );
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData(userData); // Reset to original data
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const RegUserID =
        sessionStorage.getItem("userId") || localStorage.getItem("userId");

      const response = await fetch(
        `https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList/${RegUserID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editedData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update user data");
      }

      const updatedData = await response.json();
      setUserData(updatedData);
      setEditMode(false);
      window.location.reload();
    } catch (error) {
      console.error("Error updating user data:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <div style={{ backgroundColor: "#FFF4FA" }}>
      <FromPageIndicator />
      <Container className="py-5">
        <Card className="border-0 shadow">
          <Card.Header
            className=" text-white p-4"
            style={{
              background: "linear-gradient(135deg, #3ba5c4 0%, #2582A1 100%)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="mb-0">
                {userData?.userTitle} {userData?.userName}
              </h2>
              <div className="d-flex align-items-center">
                {userData?.paymentStatus
                  ? getPaymentStatusBadge(userData.paymentStatus)
                  : getPaymentStatusBadge("NOT PAID")}
                {!editMode && (
                  <Button
                    variant="light"
                    className="ms-3"
                    onClick={handleEditClick}
                  >
                    <Pencil className="me-1" /> Update Profile
                  </Button>
                )}
                {editMode && (
                  <>
                    <Button
                      variant="success"
                      className="ms-3"
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="me-1" /> Save Changes
                        </>
                      )}
                    </Button>
                    <Button
                      variant="danger"
                      className="ms-2"
                      onClick={handleCancelEdit}
                    >
                      <X className="me-1" /> Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
            {userData?.membershipId && (
              <p className="text-light mb-0 mt-2">
                Membership ID: <strong>{userData.membershipId}</strong>
              </p>
            )}
          </Card.Header>

          <Card.Body className="p-4">
            <Row className="g-4">
              <Col lg={6}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h4 className="mb-0">
                      <Person className="me-2" />
                      Personal Information
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">Title</div>
                          {editMode ? (
                            <Form.Select
                              name="userTitle"
                              value={editedData.userTitle || ""}
                              onChange={handleInputChange}
                            >
                              <option value="Mr.">Mr.</option>
                              <option value="Ms.">Ms.</option>
                              <option value="Mrs.">Mrs.</option>
                              <option value="Dr.">Dr.</option>
                              <option value="Prof.">Prof.</option>
                            </Form.Select>
                          ) : (
                            <div className="fw-bold">
                              {userData?.userTitle || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">Full Name</div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="userName"
                              value={editedData.userName || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">{userData?.userName}</div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">Age</div>
                          {editMode ? (
                            <Form.Control
                              type="number"
                              name="age"
                              value={editedData.age || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.age || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            <Phone size={14} className="me-1" />
                            Mobile Number
                          </div>
                          <div className="fw-bold">
                            {userData?.mobileNumber}
                          </div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            <Envelope size={14} className="me-1" />
                            Email Address
                          </div>
                          <div className="fw-bold">{userData?.emailId}</div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            <Building size={14} className="me-1" />
                            Institution
                          </div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="instituteName"
                              value={editedData.instituteName || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.instituteName || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">Designation</div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="designation"
                              value={editedData.designation || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.designation || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h4 className="mb-0">
                      <GeoAlt className="me-2" />
                      Location & Contact
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">City</div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="city"
                              value={editedData.city || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.city || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            <Globe size={14} className="me-1" />
                            Country
                          </div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="country"
                              value={editedData.country || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.country || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={12}>
                        <div className="mb-3">
                          <div className="text-muted small">Postal Address</div>
                          {editMode ? (
                            <Form.Control
                              as="textarea"
                              rows={2}
                              name="postalAddress"
                              value={editedData.postalAddress || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.postalAddress || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">Postal PIN</div>
                          {editMode ? (
                            <Form.Control
                              type="text"
                              name="postalPin"
                              value={editedData.postalPin || ""}
                              onChange={handleInputChange}
                            />
                          ) : (
                            <div className="fw-bold">
                              {userData?.postalPin || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            Meal Preference
                          </div>
                          {editMode ? (
                            <Form.Select
                              name="mealPreference"
                              value={editedData.mealPreference || ""}
                              onChange={handleInputChange}
                            >
                              <option value="Vegetarian">Vegetarian</option>
                              <option value="Non-Vegetarian">
                                Non-Vegetarian
                              </option>
                              <option value="Vegan">Vegan</option>
                              <option value="No Preference">
                                No Preference
                              </option>
                            </Form.Select>
                          ) : (
                            <div className="fw-bold">
                              {userData?.mealPreference || "Not specified"}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <h4 className="mb-0">
                      <Cash className="me-2" />
                      Registration Details
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            Registration Category
                          </div>
                          <div className="fw-bold">
                            {userData?.registrationCategory
                              ? getCategoryLabel(userData?.registrationCategory)
                              : "Not specified"}
                          </div>
                        </div>
                      </Col>
                      {userData?.registrationCategory === "ampi" && (
                        <Col md={4}>
                          <div className="mb-3">
                            <div className="text-muted small">
                              MemberShip ID{" "}
                            </div>
                            <div className="fw-bold">
                              {userData?.membershipId || "Not assigned"}
                            </div>
                          </div>
                        </Col>
                      )}
                      <Col md={4}>
                        <div className="mb-3">
                          <div className="text-muted small">
                            Registration Type
                          </div>
                          <div className="fw-bold">
                            {userData?.registrationType || "Payment Pending"}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {userData?.paymentStatus !== "PAID" && (
                      <div className="mt-4 text-center">
                        <Card className="border-0 bg-light p-4">
                          <Card.Body>
                            <h5 className="mb-3">Complete Your Registration</h5>
                            <p className="mb-4">
                              Please process your payment to finalize your
                              registration.
                            </p>
                            <Button
                              variant="primary"
                              size="lg"
                              className="px-5 py-3 shadow-sm"
                              onClick={() => navigate("/paymentPage")}
                            >
                              <CreditCard className="me-2" /> Make Payment
                            </Button>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                    {userData?.paymentStatus === "PAID" && (
                      <>
                        <h3>Abstract Details</h3>
                        <Table responsive hover className="align-middle">
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Title</th>
                              <th>Category</th>
                              <th>Status</th>
                              <th>Submission Date</th>
                              <th>Last Edited</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previousAbstracts &&
                              previousAbstracts.map((abstract, index) => (
                                <tr key={abstract.id}>
                                  <td>{index + 1}</td>
                                  <td>{abstract.title}</td>
                                  <td>{abstract.category}</td>
                                  <td>
                                    {abstract.status === "accepted" ? (
                                      <>
                                        <Badge bg="success">Accepted</Badge>{" "}
                                      </>
                                    ) : abstract.status === "rejected" ? (
                                      <Badge bg="danger" className="">
                                        Rejected
                                      </Badge>
                                    ) : (
                                      <Badge bg="warning" className="">
                                        Pending
                                      </Badge>
                                    )}
                                    {abstract.abstractReferenceId !== "" && (
                                      <Badge bg="warning">
                                        Score :{" "}
                                        <strong>
                                          {abstract.presentationScore}
                                        </strong>
                                      </Badge>
                                    )}
                                    <br></br>
                                    {abstract.abstractReferenceId !== "" && (
                                      <Badge bg="info" className="me-1">
                                        Abstract Number :{" "}
                                        <strong>
                                          {abstract.abstractReferenceId}
                                        </strong>
                                      </Badge>
                                    )}
                                    <br></br>
                                    <p className="m-0">
                                      Type:
                                      <strong>{`${abstract.presentationType}`}</strong>
                                    </p>{" "}
                                  </td>

                                  <td>
                                    {format(
                                      new Date(abstract.submissionDate),
                                      "MMM d, yyyy"
                                    )}
                                  </td>
                                  <td>
                                    {format(
                                      new Date(abstract.lastEditDate),
                                      "MMM d, yyyy"
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default UserDetailsComponent;
